import React, { useState, useMemo, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { fmt, fmtDate, extractEntriesWithLabels, reconcileEntries } from '../helpers/helpers';
import { resolveEntryLabel } from '../helpers/metadataHelpers';
import store from '../stores/NotesStore';
import AttachmentsModal from './AttachmentsModal';
import HistoryModal from './HistoryModal';
import MetadataLinkModal from './MetadataLinkModal';
import EntryShareModal from './EntryShareModal';
import PaymentSourceModal from './PaymentSourceModal';
import EntryPaymentSourceModal from './EntryPaymentSourceModal';

const BillsTable = observer(({ readOnly = false, sharedNote = null }) => {
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // For shared notes, derive negatives from sharedNote data
  const sharedNegatives = useMemo(() => {
    if (!readOnly || !sharedNote) return [];
    const raw = extractEntriesWithLabels(sharedNote.content || '');
    const { entries } = reconcileEntries(raw, sharedNote);
    return entries.filter(e => e.value < 0);
  }, [readOnly, sharedNote]);

  const negatives = readOnly ? sharedNegatives : store.negatives;

  // For readOnly mode, manage selection locally
  const [localSelectedIds, setLocalSelectedIds] = useState(new Set());

  const selectedIds = readOnly ? localSelectedIds : store.selectedIds;

  const selectedSum = useMemo(() => {
    if (selectedIds.size === 0) return 0;
    return negatives
      .filter(e => selectedIds.has(e.id))
      .reduce((sum, e) => sum + e.value, 0);
  }, [negatives, selectedIds]);

  const handleRowClick = (row, e) => {
    if (readOnly) {
      // Local selection for readOnly mode - toggle on click
      setLocalSelectedIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(row.id)) {
          newSet.delete(row.id);
        } else {
          newSet.add(row.id);
        }
        return newSet;
      });
    } else {
      store.onRowClick(row, e);
    }
  };

  const clearSelection = () => {
    if (readOnly) {
      setLocalSelectedIds(new Set());
    } else {
      store.clearSelection();
    }
  };

  const [attachmentModalId, setAttachmentModalId] = useState(null);
  const [historyModalEntryId, setHistoryModalEntryId] = useState(null);
  const [metadataLinkId, setMetadataLinkId] = useState(null);
  const [shareModalEntry, setShareModalEntry] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [sourceModalEntry, setSourceModalEntry] = useState(null);
  const [paymentSourceEntry, setPaymentSourceEntry] = useState(null);

  if (negatives.length === 0) return null;

  const showSourceColumn = !readOnly && !store.isGuest;
  // "Forma" (cash / card / reminder). Guests have no backend and therefore no
  // payment sources, so the column would always be empty for them.
  const showPaymentSourceColumn = !readOnly && !store.isGuest;
  const desktopColumnCount = 3 // Pago, Descrição, Venc.
    + (!readOnly ? 1 : 0) // Cadastro
    + (showSourceColumn ? 1 : 0) // Pago com
    + (showPaymentSourceColumn ? 1 : 0) // Forma
    + 1 // Anexos
    + 1 // Info
    + (!readOnly ? 1 : 0) // share
    + 1; // Valor
  const footerColSpan = isMobile ? 4 : desktopColumnCount - 1;

  const handleAttachmentClick = (entryId) => {
    setAttachmentModalId(entryId);
  };

  const handleHistoryClick = (entry) => {
    setHistoryModalEntryId(entry.id);
  };

  const getEntryLabel = (entry) => resolveEntryLabel(entry, store);

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <table className="neg-table">
          <thead>
            <tr>
              <th>Pago</th>
              <th>Descrição</th>
              <th>Venc.</th>
              {isMobile ? (
                <th style={{ textAlign: 'center' }}>Ações</th>
              ) : (
                <>
                  {!readOnly && <th>Cadastro</th>}
                  {showSourceColumn && <th>Pago com</th>}
                  {showPaymentSourceColumn && <th>Forma</th>}
                  <th>Anexos</th>
                  <th>Info</th>
                  {!readOnly && <th></th>}
                </>
              )}
              <th style={{ textAlign: 'right' }}>Valor</th>
            </tr>
          </thead>
          <tbody>
            {negatives.map(row => {
              const attachmentCount = row.attachments?.length || 0;
              const hasAttachments = attachmentCount > 0;
              const hasHistory = row.history && row.history.length > 1;
              // Card-settled and reminder rows keep their due date, urgency and
              // checkbox but are not in the header totals — the muted amount and
              // the chip are what say so.
              const countedIn = readOnly ? 'cash' : store.countedIn(row);
              const paymentSource = readOnly ? null : store.paymentSourceById(row.paymentSourceId);

              return (
                <tr
                  key={row.id}
                  className={`${row.is_paid ? 'paid-row' : ''} ${selectedIds.has(row.id) ? 'selected-row' : ''}`}
                  onClick={(e) => handleRowClick(row, e)}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={row.is_paid}
                      onChange={readOnly ? undefined : () => store.togglePaid(row)}
                      disabled={readOnly}
                    />
                  </td>
                  <td>
                    {getEntryLabel(row)}
                  </td>
                  <td
                    onClick={readOnly ? undefined : (e) => {
                      e.stopPropagation();
                      store.setEditingDueId(row.id);
                    }}
                    style={{ cursor: readOnly ? 'default' : 'pointer', minWidth: 110 }}
                    title={readOnly ? '' : 'Clique para editar a data de vencimento'}
                  >
                    {!readOnly && store.editingDueId === row.id ? (
                      <input
                        type="date"
                        defaultValue={row.due_date || ''}
                        onBlur={(e) => {
                          store.changeDueDate(row, e.target.value);
                          store.setEditingDueId(null);
                        }}
                        autoFocus
                      />
                    ) : (
                      fmtDate(row.due_date)
                    )}
                  </td>
                  {isMobile ? (
                    // Mobile: 3-dot menu
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                      <div className="row-menu">
                        <button
                          className="icon-btn-sm"
                          onClick={() => setOpenMenuId(openMenuId === row.id ? null : row.id)}
                        >
                          <i className="fa-solid fa-ellipsis-vertical"></i>
                        </button>

                        {openMenuId === row.id && (
                          <>
                            <div
                              className="menu-backdrop"
                              onClick={() => setOpenMenuId(null)}
                            />
                            <div className="row-menu-dropdown">
                              {!readOnly && (
                                <button onClick={() => { setMetadataLinkId(row.id); setOpenMenuId(null); }}>
                                  <i className="fa-solid fa-link"></i>
                                  Cadastro
                                </button>
                              )}
                              <button onClick={() => { handleAttachmentClick(row.id); setOpenMenuId(null); }}>
                                <i className="fa-solid fa-paperclip"></i>
                                Anexos {hasAttachments && `(${attachmentCount})`}
                              </button>
                              {(!readOnly || hasHistory) && (
                                <button onClick={() => { handleHistoryClick(row); setOpenMenuId(null); }}>
                                  <i className="fa-solid fa-circle-info"></i>
                                  Histórico
                                </button>
                              )}
                              {!readOnly && hasAttachments && (
                                <button onClick={() => { setShareModalEntry(row); setOpenMenuId(null); }}>
                                  <i className="fa-solid fa-share-nodes"></i>
                                  Compartilhar
                                </button>
                              )}
                              {showSourceColumn && (
                                <button onClick={() => { setSourceModalEntry(row); setOpenMenuId(null); }}>
                                  <i className="fa-solid fa-money-bill-transfer"></i>
                                  Pago com
                                </button>
                              )}
                              {showPaymentSourceColumn && (
                                <button onClick={() => { setPaymentSourceEntry(row); setOpenMenuId(null); }}>
                                  <i className="fa-solid fa-credit-card"></i>
                                  Forma de pagamento
                                </button>
                              )}
                              <button onClick={() => {
                                store.setEditingDueId(row.id);
                                setOpenMenuId(null);
                              }}>
                                <i className="fa-regular fa-calendar"></i>
                                Vencimento
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  ) : (
                    // Desktop: individual buttons
                    <>
                      {!readOnly && (
                        <td className="metadata-link-cell" onClick={(e) => e.stopPropagation()}>
                          <button
                            className={`metadata-link-btn ${row.metadataId ? 'has-metadata' : ''}`}
                            onClick={() => setMetadataLinkId(row.id)}
                            title={row.metadataId ? `Vinculado: ${getEntryLabel(row)}` : 'Vincular a cadastro'}
                          >
                            {row.metadataId ? (
                              <i className="fas fa-link"></i>
                            ) : (
                              <i className="fas fa-link" style={{ opacity: 0.3 }}></i>
                            )}
                          </button>
                        </td>
                      )}
                      {showSourceColumn && (
                        <td className="metadata-link-cell" onClick={(e) => e.stopPropagation()}>
                          <button
                            className={`metadata-link-btn ${store.sourcesForExpense(row.id).length > 0 ? 'has-sources' : ''}`}
                            onClick={() => setSourceModalEntry(row)}
                            title={store.sourcesForExpense(row.id).length > 0 ? `Pago com: ${store.sourcesForExpense(row.id).length} fonte(s)` : 'Pago com'}
                          >
                            <i className="fa-solid fa-money-bill-transfer"></i>
                          </button>
                        </td>
                      )}
                      {showPaymentSourceColumn && (
                        <td className="metadata-link-cell" onClick={(e) => e.stopPropagation()}>
                          {countedIn === 'reminderOnly' ? (
                            <span
                              className="card-chip reminder-chip"
                              onClick={() => setPaymentSourceEntry(row)}
                              title="Só lembrete — fora dos totais"
                            >
                              <i className="fa-solid fa-bell"></i>
                              Lembrete
                            </span>
                          ) : paymentSource && paymentSource.kind === 'credit-card' ? (
                            <span
                              className="card-chip"
                              onClick={() => setPaymentSourceEntry(row)}
                              title={`${paymentSource.name} — fora dos totais`}
                            >
                              <i className="fa-solid fa-credit-card"></i>
                              {paymentSource.name}
                            </span>
                          ) : (
                            <button
                              className="metadata-link-btn"
                              onClick={() => setPaymentSourceEntry(row)}
                              title={paymentSource ? paymentSource.name : 'Definir forma de pagamento'}
                            >
                              <i
                                className="fa-solid fa-credit-card"
                                style={paymentSource ? undefined : { opacity: 0.3 }}
                              ></i>
                            </button>
                          )}
                        </td>
                      )}
                      <td className="attachments-cell" onClick={(e) => e.stopPropagation()}>
                        {readOnly && !hasAttachments ? (
                          <span style={{ color: '#ddd' }}>—</span>
                        ) : (
                          <button
                            className={`attachment-btn ${hasAttachments ? 'has-attachments' : ''}`}
                            onClick={() => handleAttachmentClick(row.id)}
                            title={hasAttachments ? `Ver anexos (${attachmentCount})` : 'Adicionar anexo'}
                          >
                            {hasAttachments ? (
                              <>
                                <i className="fas fa-eye"></i>
                                {attachmentCount > 1 && (
                                  <span className="attachment-count">{attachmentCount}</span>
                                )}
                              </>
                            ) : (
                              <i className="fas fa-paperclip"></i>
                            )}
                          </button>
                        )}
                      </td>
                      <td className="info-cell" onClick={(e) => e.stopPropagation()}>
                        {!readOnly || hasHistory ? (
                          <button
                            className="info-btn"
                            onClick={() => handleHistoryClick(row)}
                            title="Ver histórico de alterações"
                          >
                            <i className="fas fa-info-circle"></i>
                          </button>
                        ) : (
                          <span style={{ color: '#ddd' }}>—</span>
                        )}
                      </td>
                      {!readOnly && (
                        <td className="info-cell" onClick={(e) => e.stopPropagation()}>
                          {hasAttachments && (
                            <button
                              className="icon-btn-sm"
                              onClick={() => setShareModalEntry(row)}
                              title="Compartilhar comprovante"
                            >
                              <i className="fa-solid fa-share-nodes"></i>
                            </button>
                          )}
                        </td>
                      )}
                    </>
                  )}
                  <td
                    className={countedIn === 'cash' ? undefined : 'amount-excluded'}
                    style={{ textAlign: 'right' }}
                    title={countedIn === 'cash' ? undefined : 'Não entra nos totais'}
                  >
                    {isMobile && countedIn !== 'cash' && (
                      <span className={`card-chip ${countedIn === 'reminderOnly' ? 'reminder-chip' : ''}`}>
                        <i className={countedIn === 'reminderOnly' ? 'fa-solid fa-bell' : 'fa-solid fa-credit-card'}></i>
                        {countedIn === 'reminderOnly' ? 'Lembrete' : paymentSource?.name}
                      </span>
                    )}
                    {fmt(row.value)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={footerColSpan}>
                {selectedIds.size > 0 ? (
                  <span>
                    Selecionados: {selectedIds.size}{' '}
                    <button style={{ marginLeft: 8 }} onClick={clearSelection}>Limpar</button>
                  </span>
                ) : (
                  <span className="muted">Clique nas linhas para selecionar</span>
                )}
              </td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>
                {fmt(selectedSum)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Attachments Modal */}
      {attachmentModalId && (
        <AttachmentsModal
          entryId={attachmentModalId}
          entry={negatives.find(e => e.id === attachmentModalId)}
          onClose={() => setAttachmentModalId(null)}
          readOnly={readOnly}
        />
      )}

      {/* History Modal */}
      {historyModalEntryId && (
        <HistoryModal
          entryId={historyModalEntryId}
          entry={negatives.find(e => e.id === historyModalEntryId)}
          onClose={() => setHistoryModalEntryId(null)}
          readOnly={readOnly}
        />
      )}

      {/* Metadata Link Modal */}
      {metadataLinkId && (
        <MetadataLinkModal
          entry={negatives.find(e => e.id === metadataLinkId)}
          onClose={() => setMetadataLinkId(null)}
        />
      )}

      {/* Entry Share Modal */}
      {shareModalEntry && (
        <EntryShareModal
          isOpen={true}
          entry={shareModalEntry}
          allEntries={negatives}
          noteId={store.currentNoteId}
          store={store}
          onClose={() => setShareModalEntry(null)}
        />
      )}

      {/* Payment Source Modal ("Pago com" — which income funded this) */}
      {sourceModalEntry && (
        <PaymentSourceModal
          entry={sourceModalEntry}
          onClose={() => setSourceModalEntry(null)}
        />
      )}

      {/* Entry Payment Source Modal ("Forma" — cash / card / reminder) */}
      {paymentSourceEntry && (
        <EntryPaymentSourceModal
          entry={negatives.find(e => e.id === paymentSourceEntry.id) || paymentSourceEntry}
          onClose={() => setPaymentSourceEntry(null)}
        />
      )}
    </>
  );
});

export default BillsTable;
